import React, { useEffect, useMemo, useState } from "react";
import { ListTodo, Plus, Filter, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import TaskModal from "@/components/tasks/TaskModal";
import { StatusBadge, PriorityBadge, STATUS_META } from "@/components/tasks/taskMeta";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";

const initials = (name) => (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

const StatCard = ({ label, value, tone, testId }) => (
  <div data-testid={testId} className={`rounded-2xl border p-4 bg-white/60 dark:bg-slate-900/40 border-slate-200/70 dark:border-white/10`}>
    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
    <div className={`text-2xl font-extrabold font-display mt-1 ${tone}`}>{value}</div>
  </div>
);

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [modal, setModal] = useState({ open: false, initial: null });

  const load = () => endpoints.listTasks().then(setTasks).catch(() => {});
  useEffect(() => { load(); endpoints.listConsultants().then(setConsultants); }, []);

  const filtered = useMemo(() => {
    return tasks.filter((t) => {
      if (assigneeFilter !== "all" && t.assignee !== assigneeFilter) return false;
      if (statusFilter !== "all" && t.status !== statusFilter) return false;
      if (q && !`${t.title} ${t.client} ${t.category}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [tasks, assigneeFilter, statusFilter, q]);

  const stats = useMemo(() => {
    const g = { total: tasks.length, overdue: 0, in_progress: 0, waiting: 0, done: 0 };
    tasks.forEach((t) => {
      if (t.status === "Overdue") g.overdue++;
      else if (t.status === "In Progress") g.in_progress++;
      else if (t.status === "Waiting for Client") g.waiting++;
      else if (t.status === "Completed") g.done++;
    });
    return g;
  }, [tasks]);

  // Group filtered by assignee for management view
  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((t) => {
      const key = t.assignee || "Unassigned";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const consultantMeta = (name) => consultants.find((c) => c.name === name);

  const quickStatus = async (t, status) => {
    try {
      const updated = await endpoints.updateTask(t.id, { status });
      setTasks((prev) => prev.map((x) => x.id === t.id ? updated : x));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update task");
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
      <div className="glass-card rounded-3xl p-4 sm:p-5 mb-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl brand-gradient flex items-center justify-center shadow-md shadow-indigo-500/25">
            <ListTodo className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="font-display text-lg sm:text-xl font-extrabold tracking-tight">Task Manager</h1>
            <p className="text-xs text-slate-500">See what your team is working on</p>
          </div>
        </div>
        <Button data-testid="tasks-new-button" className="brand-gradient text-white ml-auto" onClick={() => setModal({ open: true, initial: null })}>
          <Plus className="h-4 w-4 mr-1" /> New task
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <StatCard testId="task-stat-total" label="All tasks" value={stats.total} tone="text-slate-800 dark:text-white" />
        <StatCard testId="task-stat-in-progress" label="In progress" value={stats.in_progress} tone="text-blue-600" />
        <StatCard testId="task-stat-waiting" label="Waiting client" value={stats.waiting} tone="text-amber-600" />
        <StatCard testId="task-stat-done" label="Completed" value={stats.done} tone="text-emerald-600" />
        <StatCard testId="task-stat-overdue" label="Overdue" value={stats.overdue} tone="text-rose-600" />
      </div>

      <div className="glass-card rounded-3xl p-4 sm:p-5 mb-4 flex flex-wrap items-center gap-3">
        <Input
          data-testid="tasks-search-input"
          placeholder="Search tasks..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs h-9"
        />
        <div className="flex items-center gap-1.5">
          <Filter className="h-3.5 w-3.5 text-slate-400" />
          <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
            <SelectTrigger data-testid="tasks-assignee-filter" className="h-9 w-[180px] text-xs">
              <SelectValue placeholder="All consultants" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All consultants</SelectItem>
              {consultants.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList data-testid="tasks-status-tabs" className="h-9 bg-slate-100 dark:bg-slate-800">
            <TabsTrigger value="all" className="text-xs h-7" data-testid="tasks-status-all">All</TabsTrigger>
            <TabsTrigger value="Not Started" className="text-xs h-7">Not started</TabsTrigger>
            <TabsTrigger value="In Progress" className="text-xs h-7">In progress</TabsTrigger>
            <TabsTrigger value="Waiting for Client" className="text-xs h-7">Waiting</TabsTrigger>
            <TabsTrigger value="Overdue" className="text-xs h-7 data-[state=active]:bg-rose-500 data-[state=active]:text-white" data-testid="tasks-status-overdue">Overdue</TabsTrigger>
            <TabsTrigger value="Completed" className="text-xs h-7">Done</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {grouped.length === 0 ? (
        <div className="glass-card rounded-3xl p-10 text-center text-sm text-slate-500">
          No tasks match your filters.
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map(([assignee, list]) => {
            const c = consultantMeta(assignee);
            const overdue = list.filter((t) => t.status === "Overdue").length;
            return (
              <div key={assignee} data-testid={`tasks-group-${assignee}`} className="glass-card rounded-3xl p-4 sm:p-5">
                <div className="flex items-center gap-3 mb-3">
                  <Avatar className="h-9 w-9 ring-2 ring-indigo-500/25">
                    <AvatarFallback className="brand-gradient text-white text-xs font-semibold">{initials(assignee)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-semibold text-sm">{assignee}</div>
                    <div className="text-[11px] text-slate-500">{c?.role || "Team member"}</div>
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    {overdue > 0 && (
                      <Badge className="bg-rose-100 text-rose-700 border-rose-200 rounded-full">
                        <AlertTriangle className="h-2.5 w-2.5 mr-1" />
                        {overdue} overdue
                      </Badge>
                    )}
                    <Badge variant="secondary" className="rounded-full">{list.length} tasks</Badge>
                  </div>
                </div>

                <ScrollArea className="max-h-[420px] pr-2">
                  <ul className="space-y-2.5">
                    {list.map((t) => {
                      const isOverdue = t.status === "Overdue";
                      return (
                        <li
                          key={t.id}
                          data-testid={`task-row-${t.id}`}
                          className={`rounded-2xl border p-3 cursor-pointer transition-all hover:shadow-md
                            ${isOverdue
                              ? "bg-rose-50/60 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/25"
                              : "bg-white/60 dark:bg-slate-900/40 border-slate-200/70 dark:border-white/10 hover:border-indigo-300/70"
                            }`}
                          onClick={() => setModal({ open: true, initial: t })}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`h-2 w-2 rounded-full mt-2 shrink-0 ${STATUS_META[t.status]?.dot || "bg-slate-400"}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-sm font-semibold truncate ${t.status === "Completed" ? "line-through text-slate-400" : ""}`}>{t.title}</span>
                                <StatusBadge status={t.status} />
                                <PriorityBadge priority={t.priority} />
                              </div>
                              <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-1 flex-wrap">
                                {t.client && <span className="truncate">📁 {t.client}</span>}
                                <span className="truncate">{t.category}</span>
                                {t.due_date && (
                                  <span className={isOverdue ? "font-semibold text-rose-600" : ""}>
                                    Due {new Date(t.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                                  </span>
                                )}
                                <span className="ml-auto font-mono">{t.progress}%</span>
                              </div>
                              <Progress value={t.progress || 0} className="h-1.5 mt-2" />
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </ScrollArea>
              </div>
            );
          })}
        </div>
      )}

      <TaskModal
        open={modal.open}
        initial={modal.initial}
        consultants={consultants}
        onOpenChange={(o) => setModal({ open: o, initial: modal.initial })}
        onChanged={() => { load(); setModal({ open: false, initial: null }); }}
      />
    </div>
  );
}
